import { ReactNode } from 'react';

import { PlaygroundOutputCodeBlock } from '@/Pages/Playground/components/PlaygroundOutputCodeBlock';
import { MessageContentPart, S3DataSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { TextWithLinks } from '../TextWithLinks';
import { ContentPartRenderer, RenderToolCalls } from './ContentPartRenderer';
import { MarkdownRenderer } from './MarkdownRenderer';
import { VirtualizedContentRenderer } from './VirtualizedContentRenderer';
import { splitMarkdownCodeBlocks } from './utils/markdown';

export function RichTextView(props: {
  content: MessageContentPart[] | string;
  toolCalls?: any;
  attachments?: Record<string, S3DataSchema>;
  fancyRendering?: boolean;
  empty?: ReactNode;
  className?: string;
  showToolCalls?: boolean;
  maxHeightForVirtualization?: string; // If provided, long text will be virtualized
  enableMarkdown?: boolean; // If true, use markdown rendering
}) {
  if (typeof props.content === 'string') {
    const toolCallsComponents = props.toolCalls &&
      props.showToolCalls &&
      (!Array.isArray(props.toolCalls) || props.toolCalls.length > 0) && (
        <RenderToolCalls
          toolCall={{
            tool_calls: Array.isArray(props.toolCalls)
              ? props.toolCalls
              : [props.toolCalls],
          }}
        />
      );

    if (props.content === '') {
      if (props.toolCalls) {
        return toolCallsComponents;
      }
      return props.empty ?? null;
    }

    const trimmedContent = props.content.trim();

    // Helper function to render with TextWithLinks
    const renderWithTextLinks = () => (
      <div className={cn('whitespace-pre-wrap text-sm', props.className)}>
        <TextWithLinks
          text={trimmedContent}
          containerHeight={props.maxHeightForVirtualization}
        />
        {toolCallsComponents}
      </div>
    );

    // Use markdown rendering if specifically enabled
    if (props.enableMarkdown) {
      return (
        <MarkdownRenderer
          content={trimmedContent}
          containerHeight={props.maxHeightForVirtualization}
          className={props.className}
          toolCallsComponents={toolCallsComponents}
        />
      );
    }

    // Use code rendering in non-playgroud fancy rendering if there's code blocks.
    // This should be removed and use react markdown once we stablize the react markdown renderer.
    const parts = splitMarkdownCodeBlocks(trimmedContent);
    const has_code_blocks = parts.length > 1;
    if (props.fancyRendering && has_code_blocks) {
      return (
        <div className={cn('flex flex-col gap-3.5 text-sm', props.className)}>
          {parts.map((part, idx) => {
            const match = part.match(/^```([A-Za-z]+)([\s\S]*?)```$/);
            if (!match) {
              return (
                part.trim() && (
                  <div
                    key={idx}
                    className="dark:prose-invert prose prose-playground flex flex-col gap-3"
                  >
                    {part.trim()}
                  </div>
                )
              );
            }

            const [, language, code] = match;
            return (
              <div key={idx}>
                {props.fancyRendering ? (
                  <PlaygroundOutputCodeBlock
                    code={code.trim()}
                    language={language}
                  />
                ) : (
                  code.trim()
                )}
              </div>
            );
          })}
          {toolCallsComponents}
        </div>
      );
    }

    // Simple text rendering when fancy rendering is disabled
    return renderWithTextLinks();
  }

  if (Array.isArray(props.content)) {
    const fallback = props.content.map((part, idx) => (
      <ContentPartRenderer
        key={idx}
        part={part}
        idx={idx}
        attachments={props.attachments}
        className={props.className}
        showToolCalls={props.showToolCalls}
        toolCalls={props.toolCalls}
      />
    ));
    // Use virtualization if maxHeightForVirtualization is provided
    if (props.maxHeightForVirtualization) {
      return (
        <VirtualizedContentRenderer
          content={props.content}
          toolCalls={props.toolCalls}
          attachments={props.attachments}
          className={props.className}
          showToolCalls={props.showToolCalls}
          maxHeightForVirtualization={props.maxHeightForVirtualization}
          fallback={fallback}
        />
      );
    }

    // Use shared component for rendering
    return fallback;
  }
  return null;
}
